import { getAhaBridge } from '@/app/bridge/ahaBridge';

type SaveBlobOptions = {
  blob: Blob;
  fileName: string;
  mimeType?: string;
  mediaKind?: 'image' | 'video' | 'file';
  saveToPhotos?: boolean;
};

type SaveBlobResult = {
  method: 'native' | 'browser';
  canceled?: boolean;
};

export function getImageFileExtension(blob: Blob): 'jpg' | 'png' {
  const contentType = String(blob.type || '').toLowerCase();
  if (contentType.includes('png')) return 'png';
  return 'jpg';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Unable to read download file.'));
    };
    reader.onerror = () =>
      reject(reader.error || new Error('Unable to read download file.'));
    reader.readAsDataURL(blob);
  });
}

async function saveBlobWithNativeApp({
  blob,
  fileName,
  mimeType,
  mediaKind,
  saveToPhotos
}: SaveBlobOptions): Promise<SaveBlobResult | null> {
  const bridge = getAhaBridge();
  if (!bridge.isNative) return null;

  const dataUrl = await blobToDataUrl(blob);
  const result = await bridge.file.saveFile({
    dataUrl,
    fileName,
    mimeType: mimeType || blob.type || 'application/octet-stream',
    mediaKind,
    saveToPhotos
  });
  if (result) return { method: 'native', canceled: result.canceled };

  if (saveToPhotos) {
    const imageResult = await bridge.file.saveImage({
      dataUrl,
      fileName
    });
    if (imageResult) return { method: 'native' };
  }

  return null;
}

function downloadBlobInBrowser(blob: Blob, fileName: string): SaveBlobResult {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { method: 'browser' };
}

export async function saveBlobWithAppSupport(
  options: SaveBlobOptions
): Promise<SaveBlobResult> {
  const nativeResult = await saveBlobWithNativeApp(options);
  if (nativeResult) return nativeResult;

  return downloadBlobInBrowser(options.blob, options.fileName);
}

export async function saveUrlWithAppSupport({
  url,
  fileName,
  saveToPhotos,
  mediaKind,
  mimeType
}: {
  url: string;
  fileName: string;
  saveToPhotos?: boolean;
  mediaKind?: 'image' | 'video' | 'file';
  mimeType?: string;
}) {
  const bridge = getAhaBridge();
  if (bridge.isNative) {
    const result = await bridge.file.saveUrl({
      url,
      fileName,
      mimeType,
      mediaKind,
      saveToPhotos
    });
    if (result) return { method: 'native' as const };
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
  return { method: 'browser' as const };
}
